/**
 * InlineEdit — the primitive that deletes *Edit · Save · Cancel* (UX cleanup, 2026-08-25).
 *
 * Three buttons per editable field is how this app ended up with thirteen controls on one
 * task. The field itself already knows everything those buttons said:
 *
 *   - **Focus** is "edit". Nothing has to be pressed to begin — the text *is* the field.
 *   - **Enter** commits, and so does **blur**. Whichever the person reaches for first, the
 *     value is saved. In a multiline field Enter is a newline, because it has to be; there
 *     **⌘/Ctrl+Enter** commits, and blur still does.
 *   - **Escape** reverts to the last committed value and lets go of the field.
 *
 * FOUR DECISIONS WORTH KNOWING BEFORE CHANGING ANYTHING HERE
 *
 * 1. **A commit that changes nothing is not a commit.** Blur fires constantly — tabbing
 *    past, clicking elsewhere on the screen, the browser losing the window. Every one of
 *    those would be a `PATCH` if the control did not compare against the last committed
 *    value first, and a register that re-versions a row because somebody tabbed through it
 *    is worse than the buttons it replaced.
 *
 * 2. **The draft lives in a ref as well as in state.** `onBlur` reads the draft, and a
 *    state variable captured by that closure is one render behind after Escape — the field
 *    would revert visually and then save the reverted-from value anyway. The ref is the
 *    value; the state is what React draws.
 *
 * 3. **`required` reverts rather than refuses.** An empty title is not a validation error
 *    worth a red sentence on a field with no submit button — there is nothing to press
 *    again. Emptying a required field and leaving puts the old value back, which is what
 *    Escape would have done and what the person meant.
 *
 * 4. **Saving neither disables nor spins.** The request has already left; the person has
 *    already moved on. Taking the field away to prove it is exactly the modal beat this
 *    control exists to remove. A hairline is the whole feedback, and a failed save is the
 *    caller's own `InlineError` beneath, as it was before.
 *
 * The value is **owned by the caller** — this is a controlled field with a local draft, so
 * a server answer arriving while somebody is typing does not overwrite what they typed, and
 * one arriving while they are not does.
 */

import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import styles from './InlineEdit.module.css';

/** `title` is a heading that happens to be editable; `body` is a paragraph that is. */
export type InlineEditVariant = 'title' | 'body';

export interface InlineEditProps {
  /**
   * The accessible name. Never drawn — the control replaces text that already sits under a
   * heading, and a second visible label would put back the form furniture this removes.
   */
  readonly label: string;
  readonly value: string;
  /** Called once per *changed* value, on Enter or on blur. Never called with the value it
      was given. */
  readonly onCommit: (next: string) => void;
  readonly variant?: InlineEditVariant | undefined;
  /** A textarea rather than an input: Enter writes a newline, ⌘/Ctrl+Enter commits. */
  readonly multiline?: boolean | undefined;
  readonly rows?: number | undefined;
  readonly maxLength?: number | undefined;
  readonly placeholder?: string | undefined;
  /** Read-only callers get the same text without the field's affordances. */
  readonly disabled?: boolean | undefined;
  /** Emptying the field puts the last committed value back instead of saving nothing. */
  readonly required?: boolean | undefined;
  /** Drawn as a hairline, not a spinner. See decision 4. */
  readonly saving?: boolean | undefined;
  /** Focus and select on mount — for a field the screen was opened in order to fill. */
  readonly autoFocus?: boolean | undefined;
}

export function InlineEdit({
  label,
  value,
  onCommit,
  variant = 'body',
  multiline = false,
  rows = 4,
  maxLength,
  placeholder,
  disabled = false,
  required = false,
  saving = false,
  autoFocus = false,
}: InlineEditProps) {
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(value);
  /** The last value the server is known to hold. Escape and the no-op check both read it. */
  const committed = useRef(value);
  const focused = useRef(false);
  // One ref for both renderings — the two branches never coexist, and the three things this
  // component asks of the node (focus, select, blur) are on the shared base type.
  const element = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // The row changed underneath — a different task mounted, or somebody else saved this one.
  // Only adopt it when nobody is typing: an incoming answer must not eat a draft.
  useEffect(() => {
    committed.current = value;
    if (focused.current) return;
    draftRef.current = value;
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!autoFocus) return;
    element.current?.focus();
    element.current?.select();
  }, [autoFocus]);

  function write(next: string) {
    draftRef.current = next;
    setDraft(next);
  }

  function revert() {
    write(committed.current);
  }

  function commit() {
    // A single-line value is trimmed because the whitespace is never meant; a multiline one
    // is sent as typed, minus trailing blank lines nobody can see.
    const next = multiline ? draftRef.current.replace(/\s+$/u, '') : draftRef.current.trim();
    if (required && next === '') {
      revert();
      return;
    }
    if (next === committed.current) {
      // Normalise the draft anyway, so a field somebody only added spaces to stops looking
      // dirty. No request: decision 1.
      write(committed.current);
      return;
    }
    committed.current = next;
    write(next);
    onCommit(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      revert();
      // Blur after reverting: the draft now equals the committed value, so the blur commit
      // is a no-op by decision 1 rather than by a flag this component would have to keep.
      element.current?.blur();
      return;
    }
    if (event.key !== 'Enter') return;
    if (multiline && !event.metaKey && !event.ctrlKey) return;
    event.preventDefault();
    // Blur rather than commit directly: one path saves, and the field lets go the way the
    // person just told it to.
    element.current?.blur();
  }

  const classes = [
    styles.control,
    styles[variant],
    multiline ? styles.multiline : '',
  ]
    .filter(Boolean)
    .join(' ');

  const shared = {
    className: classes,
    value: draft,
    'aria-label': label,
    disabled,
    onKeyDown,
    onFocus: () => {
      focused.current = true;
    },
    onBlur: () => {
      focused.current = false;
      commit();
    },
    onChange: (event: { target: { value: string } }) => {
      write(event.target.value);
    },
    ...(maxLength === undefined ? {} : { maxLength }),
    ...(placeholder === undefined ? {} : { placeholder }),
  };

  return (
    <div className={[styles.wrap, saving ? styles.saving : ''].filter(Boolean).join(' ')}>
      {multiline ? (
        <textarea
          {...shared}
          rows={rows}
          ref={(node) => {
            element.current = node;
          }}
        />
      ) : (
        <input
          {...shared}
          type="text"
          ref={(node) => {
            element.current = node;
          }}
        />
      )}
    </div>
  );
}
